import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BanknoteIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, FileTextIcon, LoaderCircleIcon, LockIcon, MapPinIcon, XIcon } from 'lucide-react';
import { cashSteps, readiness, savedCard } from '../../data/loisirs';
interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onPaid: () => void;
}
type Stage = 'choix' | 'carte' | 'traitement' | 'paye' | 'comptant';
const CASH_CODE = 'BRO-4821-7Y';
const secondaryButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
export function PaymentDialog({
  open,
  onClose,
  onPaid
}: PaymentDialogProps) {
  const [stage, setStage] = useState<Stage>('choix');
  const [useSaved, setUseSaved] = useState(true);
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);
  useEffect(() => {
    if (!open) {
      setStage('choix');
      setUseSaved(true);
    }
  }, [open]);
  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);
  const pay = () => {
    setStage('traitement');
    timerRef.current = window.setTimeout(() => {
      setStage('paye');
      onPaid();
    }, 1400);
  };
  const summary = <dl className="rounded-2xl border border-slate-200 bg-ville-sand p-4 text-sm" data-id="element-2020" data-mp="33805v16tm4w0">
      <div className="flex items-start justify-between gap-4" data-id="element-2021" data-mp="335xkwvkiz1y5">
        <div data-id="element-2022" data-mp="2fipda6zmjgsy">
          <dt className="sr-only" data-id="element-2023" data-mp="2fiflqeyxl1l5">Activité</dt>
          <dd className="font-extrabold text-ville-navy" data-id="element-2024" data-mp="2bh44awq7nmo1">{readiness.activity}</dd>
          <dd className="mt-0.5 text-slate-600" data-id="element-2025" data-mp="2p7z8vwtofhlt">
            {readiness.participant} · {readiness.session}
          </dd>
        </div>
        <div className="text-right" data-id="element-2026" data-mp="2kg7ns1tai432">
          <dt className="sr-only" data-id="element-2027" data-mp="2kf60myz01r4z">Montant</dt>
          <dd className="text-lg font-extrabold text-ville-navy" data-id="element-2028" data-mp="1wqwkackk1vl8">{readiness.amount}</dd>
          <dd className="text-xs text-slate-500" data-id="element-2029" data-mp="1rsv4q1mc8eei">{readiness.reference}</dd>
        </div>
      </div>
    </dl>;
  return <AnimatePresence data-id="element-2030" data-mp="79hthxrsafvb">
      {open && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2031" data-mp="78g6cuxhu2x8">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/45" data-id="element-2032" data-mp="397btnumq10m" />

          <motion.div role="dialog" aria-modal="true" aria-label="Paiement de l’inscription" initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-2033" data-mp="uyu1wvfaejwk">
            <div className="flex items-start justify-between gap-4" data-id="element-2034" data-mp="3m04dyl5ui5qm">
              <div data-id="element-2035" data-mp="3h82twd2fraq1">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-2036" data-mp="3h7jkxlurjwo5">
                  {stage === 'comptant' ? 'Paiement en argent comptant' : 'Paiement de l’inscription'}
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2037" data-mp="389hgewbtmlw4">
                  {stage === 'paye' ? 'Paiement reçu' : `${readiness.amount} à payer`}
                </h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2038" data-mp="4p37wsstgc3k">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-2039" data-mp="2flbu4554cou6" />
              </button>
            </div>

            <div className="mt-5" data-id="element-2040" data-mp="25d7mah1uetdr">
              {stage === 'choix' && <div data-id="element-2041" data-mp="2aewwxvl5l51b">
                  {summary}
                  <p className="mt-5 text-[15px] font-bold text-ville-navy" data-id="element-2042" data-mp="2fdrp3o50g5y3">Comment souhaitez-vous payer ?</p>
                  <div className="mt-3 space-y-3" data-id="element-2043" data-mp="2k4qa310y1s7o">
                    <button type="button" onClick={() => setStage('carte')} className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2044" data-mp="2k5ja7j5edjgu">
                      <CreditCardIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2045" data-mp="13pyg4jo5gcw9" />
                      <span data-id="element-2046" data-mp="18ors27nsfvub">
                        <span className="block text-sm font-extrabold text-ville-navy" data-id="element-2047" data-mp="18q30iks6hrrr">Carte en ligne</span>
                        <span className="block text-sm text-slate-600" data-id="element-2048" data-mp="10p52ag003oj3">
                          Confirmation immédiate · reçu envoyé par courriel
                        </span>
                      </span>
                    </button>
                    <button type="button" onClick={() => setStage('comptant')} className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2049" data-mp="1ak6r1aqd6ol5">
                      <BanknoteIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2050" data-mp="1odei91aj9i2b" />
                      <span data-id="element-2051" data-mp="22cxqrv79avf5">
                        <span className="block text-sm font-extrabold text-ville-navy" data-id="element-2052" data-mp="22e8rfm5enn21">
                          Argent comptant, au comptoir
                        </span>
                        <span className="block text-sm text-slate-600" data-id="element-2053" data-mp="2h062qy8e34s6">
                          Place gardée jusqu’au {readiness.payBefore}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>}

              {stage === 'carte' && <div data-id="element-2054" data-mp="2cx9kx9qobcpa">
                  {summary}

                  <fieldset className="mt-5" data-id="element-2055" data-mp="1y9ephlla6u8i">
                    <legend className="text-[15px] font-bold text-ville-navy" data-id="element-2056" data-mp="1y8lqj6skd90x">Moyen de paiement</legend>
                    <label className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville ${useSaved ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200'}`} data-id="element-2057" data-mp="231y3ukrslted">
                      <input type="radio" name="moyen-paiement" checked={useSaved} onChange={() => setUseSaved(true)} className="h-4 w-4 accent-ville-navy" data-id="element-2058" data-mp="1fdevk58qtgma" />
                      <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2059" data-mp="1ur90y16e8xxv" />
                      <span className="min-w-0 flex-1" data-id="element-2060" data-mp="1lsdwoumwtxx7">
                        <span className="block text-sm font-bold text-ville-navy" data-id="element-2061" data-mp="1lrvs9aq3gde7">
                          {savedCard.brand} •••• {savedCard.last4}
                        </span>
                        <span className="block text-xs text-slate-500" data-id="element-2062" data-mp="1c1j9no1uauap">Expire {savedCard.expiry}</span>
                      </span>
                    </label>

                    <label className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-150 ease-ville ${useSaved ? 'border-slate-200' : 'border-ville-indigo bg-ville-indigo-soft'}`} data-id="element-2063" data-mp="131w9t6ordwjr">
                      <input type="radio" name="moyen-paiement" checked={!useSaved} onChange={() => setUseSaved(false)} className="h-4 w-4 accent-ville-navy" data-id="element-2064" data-mp="34rlb8gnh3yi7" />
                      <span className="text-sm font-bold text-ville-navy" data-id="element-2065" data-mp="3ir4ht50mz1se">Utiliser une autre carte</span>
                    </label>
                  </fieldset>

                  {!useSaved && <div className="mt-4 space-y-3" data-id="element-2066" data-mp="2qz5bppts6im1">
                      <div data-id="element-2067" data-mp="2qxkka92rjfse">
                        <label htmlFor="carte-numero" className="block text-xs font-bold text-slate-500" data-id="element-2068" data-mp="2v1ik6lzwwumi">
                          Numéro de carte
                        </label>
                        <input id="carte-numero" inputMode="numeric" placeholder="0000 0000 0000 0000" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-2069" data-mp="2zttkvr2h8kg4" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-2070" data-mp="19aa4q03jg3eg">
                        <div data-id="element-2071" data-mp="198p8m61ksceh">
                          <label htmlFor="carte-exp" className="block text-xs font-bold text-slate-500" data-id="element-2072" data-mp="1e9mq0gerh2ex">
                            Expiration
                          </label>
                          <input id="carte-exp" placeholder="MM / AA" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-2073" data-mp="hhsxb9v5yftb" />
                        </div>
                        <div data-id="element-2074" data-mp="3u0r32pkkdwh2">
                          <label htmlFor="carte-cvc" className="block text-xs font-bold text-slate-500" data-id="element-2075" data-mp="2upxuxna5x3c">
                            Code de sécurité
                          </label>
                          <input id="carte-cvc" inputMode="numeric" placeholder="123" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none" data-id="element-2076" data-mp="3re8kadlhobro" />
                        </div>
                      </div>
                    </div>}

                  <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-2077" data-mp="33oxeuit71t77">
                    <LockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2078" data-mp="2ys71i38rxbph" />
                    Paiement traité par la passerelle sécurisée de la Ville de Brossard.
                  </p>

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2079" data-mp="2pqpjugfpx1yo">
                    <button type="button" onClick={() => setStage('choix')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2080" data-mp="2kth40mi1la0l">
                      Retour
                    </button>
                    <button type="button" onClick={pay} className={primaryButton} data-id="element-2081" data-mp="265z7ibemvlp9">
                      Payer {readiness.amount}
                    </button>
                  </div>
                </div>}

              {stage === 'traitement' && <div className="py-10 text-center" data-id="element-2082" data-mp="2k5qie39uslz1">
                  <LoaderCircleIcon className="mx-auto h-8 w-8 animate-spin text-ville-indigo" aria-hidden="true" data-id="element-2083" data-mp="2k4ymerccgm88" />
                  <p className="mt-4 text-sm font-semibold text-ville-navy" role="status" data-id="element-2084" data-mp="3ct46rczhk97j">
                    Traitement du paiement…
                  </p>
                </div>}

              {stage === 'paye' && <div data-id="element-2085" data-mp="163j1c3s2qftj">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2086" data-mp="116sij8ovx64f">
                    <CheckIcon className="h-6 w-6" data-id="element-2087" data-mp="1awc0mc4rwvy8" />
                  </span>
                  <h3 className="mt-3 text-lg font-extrabold text-ville-navy" data-id="element-2088" data-mp="1aygffp47nfda">
                    Inscription payée et confirmée
                  </h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-2089" data-mp="i8g5vcyrle43">
                    {readiness.amount} réglé sur {savedCard.brand} •••• {savedCard.last4}. La place d’
                    {readiness.participant} est confirmée pour {readiness.session}.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3" data-id="element-2090" data-mp="27mco87tep6dy">
                    <Link to="/mon-profil" className={secondaryButton} data-id="element-2091" data-mp="27kruulitvtho">
                      <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-2092" data-mp="22mi0gd4xgwx4" />
                      Voir mon reçu
                    </Link>
                    <button type="button" className={secondaryButton} data-id="element-2093" data-mp="2xzsr1rr5aaw0">
                      <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-2094" data-mp="32xb5fxnxlibe" />
                      Ajouter au calendrier
                    </button>
                  </div>
                  <button type="button" onClick={onClose} className={`mt-4 w-full ${primaryButton}`} data-id="element-2095" data-mp="3cv8psa2o6uyq">
                    Terminer
                  </button>
                </div>}

              {stage === 'comptant' && <div data-id="element-2096" data-mp="3ltuaaarpck25">
                  {summary}

                  <div className="mt-5 rounded-2xl border-2 border-ville-navy bg-white p-4 text-center" data-id="element-2097" data-mp="3qm3t76uegjux">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-2098" data-mp="3vlq7ljlys65b">
                      Code de paiement
                    </p>
                    <p className="mt-1 text-2xl font-extrabold tracking-[0.18em] text-ville-navy" data-id="element-2099" data-mp="4dcd0tgleu4i">
                      {CASH_CODE}
                    </p>
                    <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-500" data-id="element-2100" data-mp="1szkvawrexuz9">
                      <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2101" data-mp="1ovmu8gij2a3k" />
                      Place gardée jusqu’au {readiness.payBefore}
                    </p>
                  </div>

                  <ol className="mt-5 space-y-4" data-id="element-2102" data-mp="1f0l5vmw2tc21">
                    {cashSteps.map((step, index) => <li key={step.title} className="flex gap-3" data-id="element-2103" data-mp="2cm8z5yx8je9c" data-mpi={index}>
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ville-navy text-xs font-bold text-white" aria-hidden="true" data-id="element-2104" data-mp="27nfqqkwt2dg1">
                          {index + 1}
                        </span>
                        <div className="min-w-0" data-id="element-2105" data-mp="2vebm1s0gzqs1">
                          <p className="text-sm font-bold text-ville-navy" data-id="element-2106" data-mp="2vf3a8hrqmm8a">{step.title}</p>
                          <p className="mt-0.5 text-sm text-slate-600" data-id="element-2107" data-mp="2qkgxob9x6nmm">{step.detail}</p>
                        </div>
                      </li>)}
                  </ol>

                  <p className="mt-4 flex items-start gap-1.5 text-xs text-slate-500" data-id="element-2108" data-mp="spthnz6g5ov7">
                    <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-2109" data-mp="eyopfi5eryuw" />
                    Sans paiement avant l’échéance, la place est remise aux personnes en liste d’attente.
                  </p>

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-2110" data-mp="534uoz2kz67q">
                    <button type="button" onClick={() => setStage('choix')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2111" data-mp="a1eu5lvc8sz5">
                      Retour
                    </button>
                    <button type="button" onClick={onClose} className={primaryButton} data-id="element-2112" data-mp="1vcom8k8yj8dx">
                      J’ai noté le code
                    </button>
                  </div>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}